import { useMemo } from 'react';
import { useGlobalStoreSelector } from '@/stores/global.store';

export type ModuleKey =
	| 'sales'
	| 'offers'
	| 'inspection'
	| 'invoicing'
	| 'forms'
	| 'staffing'
	| 'courses'
	| 'competence'
	| 'bookings'
	| 'checklist'
	| 'recordAndSend'
	| 'chain'
	| 'intranet';

// Offers, invoicing and forms live inside Sales, so they turn off whenever Sales is off.
const SALES_DEPENDENT_MODULE_KEYS: readonly ModuleKey[] = ['offers', 'invoicing', 'forms'];

type ModulePathPrefix = {
	prefix: string;
	module: ModuleKey;
};

// Records (contacts/companies) and their field settings stay free of the Sales add-on wall.
const RECORDS_PREFIXES: readonly string[] = [
	'/crm/contacts',
	'/crm/companies',
	'/crm/settings/contact-fields',
	'/crm/settings/company-fields',
];

// Only contacts and their fields survive a disabled Sales module; companies go with it.
const MODULE_EXEMPT_PREFIXES: readonly string[] = ['/crm/contacts', '/crm/settings/contact-fields'];

const matchesPrefix = ({ path, prefixes }: { path: string; prefixes: readonly string[] }): boolean =>
	prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

// Longest-prefix wins, so more specific paths sit above their parents (offer-defaults before /crm).
const MODULE_PATH_PREFIXES: readonly ModulePathPrefix[] = [
	{ prefix: '/crm/settings/offer-defaults', module: 'offers' },
	{ prefix: '/inspection', module: 'inspection' },
	{ prefix: '/orders', module: 'sales' },
	{ prefix: '/invoicing', module: 'sales' },
	{ prefix: '/forms', module: 'forms' },
	{ prefix: '/staffing', module: 'staffing' },
	{ prefix: '/chain/staffing', module: 'staffing' },
	{ prefix: '/courses', module: 'courses' },
	{ prefix: '/my/learning', module: 'courses' },
	{ prefix: '/team-learning', module: 'courses' },
	{ prefix: '/chain/courses', module: 'courses' },
	{ prefix: '/chain/learning', module: 'courses' },
	{ prefix: '/chain/competence', module: 'competence' },
	{ prefix: '/competence', module: 'competence' },
	{ prefix: '/checklist', module: 'checklist' },
	{ prefix: '/bookings', module: 'bookings' },
	{ prefix: '/record-and-send', module: 'recordAndSend' },
	{ prefix: '/member-portal', module: 'intranet' },
	{ prefix: '/resources', module: 'chain' },
	{ prefix: '/chain', module: 'chain' },
	{ prefix: '/crm', module: 'sales' },
];

export function isRecordsPath(pathname: string): boolean {
	return matchesPrefix({ path: pathname.toLowerCase(), prefixes: RECORDS_PREFIXES });
}

// The module a pathname belongs to, or undefined for module-agnostic routes (dashboard, settings).
export function moduleForPath(pathname: string): ModuleKey | undefined {
	const path = pathname.toLowerCase();
	if (matchesPrefix({ path, prefixes: MODULE_EXEMPT_PREFIXES })) return undefined;
	const match = MODULE_PATH_PREFIXES.find((entry) => path === entry.prefix || path.startsWith(`${entry.prefix}/`));
	return match?.module;
}

type ModuleCheck = (key: ModuleKey) => boolean;

export function moduleCheckFor(disabledModules: readonly string[] | undefined): ModuleCheck {
	const disabled = new Set(disabledModules ?? []);
	return (key) => {
		if (disabled.has(key)) return false;
		if (SALES_DEPENDENT_MODULE_KEYS.includes(key) && disabled.has('sales')) return false;
		return true;
	};
}

export function useModuleAccess() {
	const disabledModules = useGlobalStoreSelector((s) => s.currentCompany?.disabledModules);

	return useMemo(
		() => ({ disabledModules: new Set(disabledModules ?? []), isModuleEnabled: moduleCheckFor(disabledModules) }),
		[disabledModules],
	);
}

export type InvoicingMode = 'vadio' | 'upload' | 'none';

// Mirrors the backend's invoicingModeOf: no Sales means no invoicing at all; Sales without the
// Invoicing module means the company uploads the invoice it issued elsewhere.
export function invoicingModeFor(isModuleEnabled: ModuleCheck): InvoicingMode {
	if (!isModuleEnabled('sales')) return 'none';
	return isModuleEnabled('invoicing') ? 'vadio' : 'upload';
}

export function invoicingModeOf(disabledModules: readonly string[] | undefined): InvoicingMode {
	return invoicingModeFor(moduleCheckFor(disabledModules));
}

export function useInvoicingMode(): InvoicingMode {
	const { isModuleEnabled } = useModuleAccess();
	return invoicingModeFor(isModuleEnabled);
}
